import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { Card, CardContent } from '@repo/ui/card'
import {
	getAdminUsersIdActivity,
	getAdminUsersIdNotificationPreferences,
	getAdminUsersIdSecuritySummary
} from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, BellRing, KeyRound, ShieldCheck } from 'lucide-react'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { QueryInlineError } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'

export const Route = createFileRoute('/users/$id')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({ cursor: z.string().optional() }),
	component: UserDetailPage
})

function UserDetailPage() {
	const { t } = useTranslation()
	const { id } = Route.useParams()
	const { cursor } = Route.useSearch()
	const navigate = useNavigate()
	const securityQuery = useQuery({
		queryKey: ['backoffice', 'users', id, 'security'],
		queryFn: () =>
			getAdminUsersIdSecuritySummary({ path: { id } }).then(
				(response) => response.data.data
			)
	})
	const activityQuery = useQuery({
		queryKey: ['backoffice', 'users', id, 'activity', cursor],
		queryFn: () =>
			getAdminUsersIdActivity({
				path: { id },
				query: { ...(cursor ? { cursor } : {}), limit: 20 }
			}).then((response) => response.data)
	})
	const notificationPreferencesQuery = useQuery({
		queryKey: ['backoffice', 'users', id, 'notification-preferences'],
		queryFn: () =>
			getAdminUsersIdNotificationPreferences({ path: { id } }).then(
				(response) => response.data.data
			)
	})
	const security = securityQuery.data
	const activity = activityQuery.data?.data ?? []
	const notificationPreferences = notificationPreferencesQuery.data
	const loadingDetails = t('common:loading_user_details')
	const securityState = securityQuery.isPending ? loadingDetails : undefined
	const activityState = getQueryState(activityQuery.isPending, loadingDetails)
	const notificationPreferencesState = getQueryState(
		notificationPreferencesQuery.isPending,
		loadingDetails
	)
	const mfaValue =
		securityState ??
		(security?.mfaEnabled ? t('common:yes') : t('common:no'))
	const totpDevicesValue =
		securityState ?? String(security?.totpDevicesCount ?? '—')
	const passkeysValue =
		securityState ?? String(security?.passkeysCount ?? '—')
	const recoveryCodesValue =
		securityState ?? String(security?.recoveryCodesRemaining ?? '—')

	return (
		<BackofficeShell>
			<Button render={<Link to='/users' />} size='sm' variant='ghost'>
				<ArrowLeft />
				{t('common:users')}
			</Button>
			<header className='mt-6'>
				<p className='font-medium text-primary text-sm'>
					{t('common:user_security')}
				</p>
				<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
					{id}
				</h1>
			</header>
			<section className='mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
				<SecurityMetric
					label={t('common:mfa_enabled')}
					value={mfaValue}
				/>
				<SecurityMetric
					label={t('common:authenticator_devices')}
					value={totpDevicesValue}
				/>
				<SecurityMetric
					label={t('common:passkeys')}
					value={passkeysValue}
				/>
				<SecurityMetric
					label={t('common:recovery_codes')}
					value={recoveryCodesValue}
				/>
			</section>
			{securityQuery.isError ? (
				<QueryInlineError
					message={t('common:user_detail_error')}
					onRetry={() => securityQuery.refetch()}
				/>
			) : null}
			<Card className='mt-8 overflow-hidden rounded-2xl'>
				<div className='flex items-center justify-between border-b p-5'>
					<div>
						<h2 className='font-semibold'>
							{t('common:activity')}
						</h2>
						<p className='mt-1 text-muted-foreground text-sm'>
							{t('common:user_activity_hint')}
						</p>
					</div>
					<Badge variant='secondary'>
						{activityQuery.data?.meta.total ?? 0}
					</Badge>
				</div>
				<CardContent className='p-0'>
					{activity.map((item) => (
						<div
							className='flex gap-4 border-b p-5 last:border-0'
							key={item.id}
						>
							<div className='grid size-10 place-items-center rounded-xl bg-primary/10 text-primary'>
								<KeyRound className='size-4' />
							</div>
							<div className='min-w-0 flex-1'>
								<p className='font-medium'>
									{item.type.replaceAll('_', ' ')}
								</p>
								<p className='mt-1 text-muted-foreground text-sm'>
									{item.ipAddress ?? t('common:unknown_ip')}
									{' · '}
									{new Date(item.createdAt).toLocaleString()}
								</p>
							</div>
						</div>
					))}
					{activityState ? (
						<p className='p-8 text-muted-foreground'>
							{activityState}
						</p>
					) : null}
					{activityQuery.isError ? (
						<div className='p-5'>
							<QueryInlineError
								message={t('common:user_detail_error')}
								onRetry={() => activityQuery.refetch()}
							/>
						</div>
					) : null}
					{!(activityState || activityQuery.isError) &&
					activity.length === 0 ? (
						<p className='p-8 text-muted-foreground'>
							{t('common:empty_activity')}
						</p>
					) : null}
				</CardContent>
			</Card>
			<Card className='mt-8 overflow-hidden rounded-2xl'>
				<div className='flex items-center gap-3 border-b p-5'>
					<div className='grid size-10 place-items-center rounded-xl bg-primary/10 text-primary'>
						<BellRing className='size-4' />
					</div>
					<div>
						<h2 className='font-semibold'>
							{t('common:notification_preferences')}
						</h2>
						<p className='mt-1 text-muted-foreground text-sm'>
							{t('common:notification_preferences_hint')}
						</p>
					</div>
				</div>
				<CardContent className='divide-y p-0'>
					{Object.entries(notificationPreferences ?? {}).map(
						([type, preference]) => (
							<div
								className='flex items-center justify-between gap-4 p-5'
								key={type}
							>
								<p className='font-medium capitalize'>
									{type.replaceAll('_', ' ')}
								</p>
								<Badge variant='secondary'>
									{t('common:mobile_push')}:{' '}
									{preference.fcm
										? t('common:enabled')
										: t('common:disabled')}
								</Badge>
							</div>
						)
					)}
					{notificationPreferencesState ? (
						<p className='p-8 text-muted-foreground text-sm'>
							{notificationPreferencesState}
						</p>
					) : null}
					{notificationPreferencesQuery.isError ? (
						<div className='p-5'>
							<QueryInlineError
								message={t('common:user_detail_error')}
								onRetry={() =>
									notificationPreferencesQuery.refetch()
								}
							/>
						</div>
					) : null}
				</CardContent>
			</Card>
			<Button
				className='mt-5'
				disabled={!activityQuery.data?.meta.nextCursor}
				onClick={() =>
					navigate({
						to: Route.fullPath,
						params: { id },
						search: {
							cursor:
								activityQuery.data?.meta.nextCursor ?? undefined
						}
					})
				}
				variant='outline'
			>
				{t('common:next')}
			</Button>
		</BackofficeShell>
	)
}

function SecurityMetric(props: { label: string; value: string }) {
	return (
		<Card className='rounded-2xl'>
			<CardContent className='p-5'>
				<ShieldCheck className='size-5 text-primary' />
				<p className='mt-5 font-semibold text-2xl'>{props.value}</p>
				<p className='mt-1 text-muted-foreground text-sm'>
					{props.label}
				</p>
			</CardContent>
		</Card>
	)
}

function getQueryState(
	isPending: boolean,
	loading: string
): string | undefined {
	if (isPending) {
		return loading
	}
}
